/**
 * OrganizationHealthieProvidersTab Component
 * 
 * Displays paginated list of organization members (providers/staff) from Healthie
 * Uses Material-UI Table with pagination
 */
import React, { useState, useEffect } from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Typography, Alert, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const OrganizationHealthieProvidersTab = () => {
    const record = useRecordContext();
    
    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [providers, setProviders] = useState([]);
    const [totalProviders, setTotalProviders] = useState(0);
    const [loading, setLoading] = useState(true);
    
    // Check if healthie_data exists and has content
    const healthieData = record?.healthie_data;
    const hasClientGroupId = record?.client_group_id;
    
    // Load providers when component mounts or pagination changes
    useEffect(() => {
        if (healthieData?.organization_members) {
            loadProviders();
        } else {
            setLoading(false);
        }
    }, [healthieData?.organization_members, page, rowsPerPage]);
    
    const loadProviders = () => {
        setLoading(true);
        try {
            // Get organization members from healthie_data
            const allMembers = healthieData?.organization_members || [];
            const startIndex = page * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;
            
            setProviders(allMembers.slice(startIndex, endIndex));
            setTotalProviders(allMembers.length);
        } catch (error) {
            console.error('Error loading providers:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    // No Healthie UserGroup ID
    if (!hasClientGroupId) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        This organization is not linked to a Healthie Client Group.
                    </Typography>
                </Alert>
            </Box>
        );
    }
    
    // Healthie enrichment failed or no data
    if (!healthieData) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="warning" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        <strong>Unable to load Healthie data</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        The EHR system might be temporarily unavailable, the request may have timed out, or you don't have permission to view this data. 
                        Please refresh the page to try again. Note: Some queries can take 15-30 seconds for organizations with many patients.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                    Pacify Providers ({totalProviders})
                </Typography>
                <Chip 
                    label="Serving this organization" 
                    size="small" 
                    color="secondary" 
                    variant="outlined"
                    icon={<CheckCircleOutlineIcon />}
                />
            </Box>
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={1}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Healthie ID</strong></TableCell>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Active Provider</strong></TableCell>
                                <TableCell><strong>Admin</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {providers.map((provider, index) => (
                                <TableRow key={provider.id || index}>
                                    <TableCell>{provider.id || 'N/A'}</TableCell>
                                    <TableCell>{provider.full_name || `${provider.first_name} ${provider.last_name}` || 'N/A'}</TableCell>
                                    <TableCell>{provider.email || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={provider.is_active_provider ? 'Yes' : 'No'} 
                                            size="small"
                                            color={provider.is_active_provider ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={provider.is_org_admin ? 'Yes' : 'No'} 
                                            size="small"
                                            color={provider.is_org_admin ? 'info' : 'default'}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {providers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No organization members found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalProviders}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default OrganizationHealthieProvidersTab;
